/**
 * CommunityPulse widget -- Active challenges, messages, tournaments.
 */

import type { Widget, WidgetContext } from '../types';

interface CommunityData {
  activeChallenges: Array<{ title: string; progress: number; daysLeft: number }>;
  newMessages: number;
  tournamentActive: boolean;
}

function getMockData(): CommunityData {
  try {
    const raw = localStorage.getItem('aweni_community_pulse');
    if (raw) return JSON.parse(raw);
  } catch { /* */ }
  return {
    activeChallenges: [
      { title: 'Weekly Reading', progress: 60, daysLeft: 3 },
    ],
    newMessages: 3,
    tournamentActive: false,
  };
}

export const CommunityPulse: Widget = {
  id: 'community-pulse',
  label: { en: 'Community', fr: 'Communaute', ar: 'المجتمع', es: 'Comunidad', de: 'Gemeinschaft' },
  icon: '👥',
  defaultVisible: true,
  defaultOrder: 12,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    const data = getMockData();

    if (size === 'compact') {
      const parts: string[] = [];
      if (data.activeChallenges.length > 0) parts.push(`${data.activeChallenges.length} ${ctx.t('widget_challenges')}`);
      if (data.newMessages > 0) parts.push(`${data.newMessages} ${ctx.t('widget_new_messages')}`);
      if (data.tournamentActive) parts.push(ctx.t('widget_tournament_live'));

      container.innerHTML = `
        <div class="rounded-2xl p-4 cursor-pointer flex items-center justify-between" style="background:var(--card-bg);box-shadow:var(--shadow-sm);" data-nav="community">
          <div class="flex items-center gap-2">
            <span>👥</span>
            <span class="text-sm" style="color:var(--text-primary);">${parts.join(' · ') || ctx.t('widget_community_quiet')}</span>
          </div>
        </div>`;
    } else {
      container.innerHTML = `
        <div class="rounded-2xl p-5" style="background:var(--card-bg);box-shadow:var(--shadow-sm);">
          <h3 class="text-sm font-semibold uppercase tracking-wide mb-3" style="color:var(--text-secondary);">👥 ${ctx.t('widget_community_title')}</h3>

          ${data.activeChallenges.length > 0 ? `
            <div class="space-y-2 mb-3">
              ${data.activeChallenges.map((c) => `
                <div class="p-3 rounded-xl" style="background:var(--bg-secondary);">
                  <div class="flex items-center justify-between mb-1">
                    <span class="text-xs font-medium" style="color:var(--text-primary);">${c.title}</span>
                    <span class="text-xs" style="color:var(--text-secondary);">${ctx.t('community_days_left', { count: c.daysLeft })}</span>
                  </div>
                  <div class="rounded-full overflow-hidden" style="height:5px;background:var(--border);">
                    <div class="rounded-full" style="height:100%;width:${c.progress}%;background:#8B5CF6;"></div>
                  </div>
                </div>
              `).join('')}
            </div>
          ` : ''}

          ${data.newMessages > 0 ? `
            <div class="flex items-center gap-2 p-2 rounded-xl" style="background:var(--bg-secondary);">
              <span>💬</span>
              <span class="text-sm" style="color:var(--text-primary);">${data.newMessages} ${ctx.t('widget_new_messages')}</span>
            </div>
          ` : ''}

          <button class="mt-3 w-full text-xs font-medium py-2 rounded-xl transition-transform active:scale-95" style="background:#6366F120;color:#6366F1;" data-nav="community">
            ${ctx.t('widget_view_community')}
          </button>
        </div>`;
    }

    container.querySelectorAll<HTMLElement>('[data-nav]').forEach((el) => {
      el.addEventListener('click', () => ctx.navigate('community'));
    });
  },
};
